/**
 * Coding usage in words (board usage-r1, "What the founder sees"): how much you built this week, whatever the agent,
 * then each agent's limit and when it resets, where the agent tells us. Pure: the pages pass the clock and time zone.
 */
import type { AdminUsageRow, AgentWeek, LimitKind, UsageAgentName, UsageLimit, UsageWeek } from './schemas'

export const AGENT_NAME: Record<UsageAgentName, string> = { claude: 'Claude', codex: 'Codex', grok: 'Grok' }

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** 0 → "no time yet", 45 → "45 minutes", 90 → "1.5 hours", 660 → "11 hours". */
export function buildTime(minutes: number): string {
  if (minutes <= 0) return 'no time yet'
  if (minutes < 60) return plural(Math.round(minutes), 'minute')
  const h = minutes / 60
  const shown = h < 10 ? Math.round(h * 10) / 10 : Math.round(h)
  return `${shown} ${shown === 1 ? 'hour' : 'hours'}`
}

/** Short hours for tables: "11 h", "1.5 h", "40 m", "–". */
export function shortTime(minutes: number): string {
  if (minutes <= 0) return '–'
  if (minutes < 60) return `${Math.round(minutes)} m`
  const h = minutes / 60
  return `${h < 10 ? Math.round(h * 10) / 10 : Math.round(h)} h`
}

/** 1234567 → "1.2M", 34000 → "34k", 900 → "900". */
export function tokenCount(n: number): string {
  if (n >= 1e9) return `${Math.round(n / 1e8) / 10}B`
  if (n >= 1e6) return `${Math.round(n / 1e5) / 10}M`
  if (n >= 1e3) return `${Math.round(n / 1e3)}k`
  return String(Math.round(n))
}

/** "11 hours building across 9 sessions · 23 commits · 6 deploys". Deploys are left out when unknown. */
export function weekHeadline(w: UsageWeek & { deploys?: number | null }): string {
  if (w.activeMinutes <= 0 && w.sessions === 0 && w.commits === 0) return 'Nothing built yet this week.'
  const parts = [`${buildTime(w.activeMinutes)} building across ${plural(w.sessions, 'session')}`, plural(w.commits, 'commit')]
  if (w.deploys !== undefined && w.deploys !== null) parts.push(plural(w.deploys, 'deploy'))
  return parts.join(' · ')
}

/** Last week, in plain words; empty when nothing was built. */
export function lastWeekWords(previous: UsageWeek): string {
  return previous.activeMinutes > 0 ? `Last week: ${buildTime(previous.activeMinutes)}, ${plural(previous.commits, 'commit')}.` : ''
}

const BLOCKS = ['▁', '▂', '▃', '▄', '▅', '▆', '▇', '█']

/** One bar per day, scaled to the busiest day; a day with nothing is a dot. */
export function dayBars(days: UsageWeek['days']): { day: string; bar: string; words: string }[] {
  const max = Math.max(0, ...days.map((d) => d.activeMinutes))
  return days.map((d) => ({
    day: d.day,
    bar: d.activeMinutes <= 0 || max === 0 ? '·' : BLOCKS[Math.min(7, Math.max(0, Math.ceil((d.activeMinutes / max) * 8) - 1))]!,
    words: `${d.day}: ${d.activeMinutes > 0 ? buildTime(d.activeMinutes) : 'nothing'}`,
  }))
}

const timeOf = (d: Date, tz: string) => new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(d).replace(' AM', ' am').replace(' PM', ' pm')
const dayOf = (d: Date, tz: string) => new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'long' }).format(d)
const dateKey = (d: Date, tz: string) => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)

/** "resets at 3:00 pm", "resets tomorrow at 9:00 am", "resets Thursday 9:00 am", "has reset since". */
export function resetWords(resetsAt: string | null, now: number, tz: string): string {
  if (!resetsAt) return ''
  const at = new Date(resetsAt)
  if (at.getTime() <= now) return 'has reset since'
  const today = dateKey(new Date(now), tz)
  const tomorrow = dateKey(new Date(now + 86_400_000), tz)
  const day = dateKey(at, tz)
  if (day === today) return `resets at ${timeOf(at, tz)}`
  if (day === tomorrow) return `resets tomorrow at ${timeOf(at, tz)}`
  if (at.getTime() - now < 6.5 * 86_400_000) return `resets ${dayOf(at, tz)} ${timeOf(at, tz)}`
  return `resets ${new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric' }).format(at)}`
}

/** What a window is called: Claude says "session" for its five-hour window, Codex "five-hour". */
export function kindName(agent: UsageAgentName, kind: LimitKind): string {
  if (kind === 'five_hour') return agent === 'claude' ? 'Session' : 'Five-hour'
  if (kind === 'weekly') return 'This week'
  return 'One model, this week'
}

export interface LimitView {
  key: string
  name: string
  percent: number
  /** "62% used, resets at 3:00 pm". */
  words: string
  full: boolean
}

/** Each limit of one agent, the windows in a fixed order. A reading from before its reset says it may be lower now. */
export function limitViews(a: AgentWeek, now: number, tz: string): LimitView[] {
  return a.limits.map((l: UsageLimit) => {
    const reset = resetWords(l.resetsAt, now, tz)
    const words = reset === 'has reset since' ? `${l.percent}% when last seen; it has reset since` : `${l.percent}% used${reset ? `, ${reset}` : ''}`
    return { key: l.kind, name: kindName(a.agent, l.kind), percent: l.percent, words, full: l.percent >= 100 && reset !== 'has reset since' }
  })
}

/** "As of 2:31 pm" when the newest reading is more than 45 minutes old; empty when fresh. */
export function seenWords(a: AgentWeek, now: number, tz: string): string {
  const newest = Math.max(0, ...a.limits.map((l) => Date.parse(l.seenAt)))
  if (!newest || now - newest < 45 * 60_000) return ''
  return dateKey(new Date(newest), tz) === dateKey(new Date(now), tz) ? `As of ${timeOf(new Date(newest), tz)}.` : `As of ${dayOf(new Date(newest), tz)} ${timeOf(new Date(newest), tz)}.`
}

/** The line under an agent without limits: Grok publishes none, and the others haven't reported one yet. */
export function noLimitWords(a: AgentWeek): string {
  if (!a.limitsKnowable) return `${AGENT_NAME[a.agent]} doesn't report its limits on your computer.`
  return a.agent === 'claude'
    ? 'Your Claude limits show here once Claude is signed in with a Pro or Max plan.'
    : 'Your Codex limits show here after your next Codex session.'
}

/** When a window ran out: when it comes back and what to do meanwhile. Null while nothing is out. */
export function outOfLimit(agents: AgentWeek[], now: number, tz: string): string | null {
  for (const a of agents) {
    const out = a.limits.find((l) => l.percent >= 100 && (!l.resetsAt || Date.parse(l.resetsAt) > now))
    if (!out) continue
    const name = AGENT_NAME[a.agent]
    const window = out.kind === 'five_hour' ? (a.agent === 'claude' ? 'session limit' : 'five-hour limit') : 'weekly limit'
    const when = resetWords(out.resetsAt, now, tz)
    const other = a.agent === 'claude' ? 'Codex' : 'Claude'
    const extra = a.agent === 'claude' ? " Your plan may also have a free reset in Claude's Settings > Usage." : ''
    return `${name}'s ${window} ${when || 'resets soon'}. Until then, switch to ${other} in Paseo.${extra}`
  }
  return null
}

/** The admin's line for a founder: last 7 days against the 7 before. */
export function adminWeekWords(r: AdminUsageRow): string {
  const w = r.week
  const before = r.previousWeek.activeMinutes > 0 ? ` (${shortTime(r.previousWeek.activeMinutes)} the week before)` : ''
  return `${shortTime(w.activeMinutes)} in 7 days${before} · ${plural(w.sessions, 'session')} · ${plural(w.commits, 'commit')}${r.deploys3d !== null ? ` · ${plural(r.deploys3d, 'deploy')} in 3 days` : ''}`
}
